/**
 * Oct-2026 · WIF Status Card
 *
 * Shows which Workload Identity Federation / OIDC env vars are detected on
 * the current pod, with per-provider setup instructions. Displayed inside
 * the Admin "API Keys" panel so operators can verify federation health at a
 * glance without the server ever exposing key values.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle2, XCircle, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

// Use the shared axios instance so the admin session cookie is forwarded.
import axios from 'axios';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fetchWifStatus = () =>
  axios.get(`${BACKEND}/api/admin/wif-status`, { withCredentials: true }).then(r => r.data);

const PROVIDER_META = [
  {
    key: 'anthropic',
    label: 'Anthropic',
    color: 'text-amber-500',
    wifVar: 'ANTHROPIC_FEDERATION_RULE_ID',
    staticVar: 'ANTHROPIC_API_KEY',
    docs: 'https://docs.anthropic.com/en/docs/workload-identity',
    setupSteps: [
      'Create an OIDC identity provider in your Anthropic Console → Security → Federation.',
      'Set the issuer URL to your Kubernetes OIDC issuer (e.g. https://container.googleapis.com/v1/projects/…/locations/…/clusters/…).',
      'Inject the resulting Federation Rule ID into the pod as ANTHROPIC_FEDERATION_RULE_ID.',
      'Leave ANTHROPIC_API_KEY unset — the SDK will exchange the pod OIDC token for a short-lived API credential automatically.',
    ],
    vars: ['ANTHROPIC_API_KEY', 'ANTHROPIC_FEDERATION_RULE_ID', 'ANTHROPIC_WORKSPACE_ID'],
  },
  {
    key: 'openai',
    label: 'OpenAI',
    color: 'text-emerald-500',
    wifVar: 'OPENAI_FEDERATION_ISSUER_URL',
    staticVar: 'OPENAI_API_KEY',
    docs: 'https://platform.openai.com/docs/guides/workload-identity',
    setupSteps: [
      'In OpenAI Platform → Settings → Security → Federated Identities, add your K8s OIDC issuer URL.',
      'Set OPENAI_FEDERATION_ISSUER_URL to that issuer URL on the pod.',
      'Optionally set OPENAI_ORG_ID / OPENAI_PROJECT_ID for project scoping.',
      'Remove OPENAI_API_KEY — the OpenAI SDK auto-discovers the pod identity token.',
    ],
    vars: ['OPENAI_API_KEY', 'OPENAI_FEDERATION_ISSUER_URL', 'OPENAI_ORG_ID', 'OPENAI_PROJECT_ID'],
  },
  {
    key: 'gemini',
    label: 'Google Gemini (GCP ADC)',
    color: 'text-sky-500',
    wifVar: 'GOOGLE_APPLICATION_CREDENTIALS',
    staticVar: 'GEMINI_API_KEY',
    docs: 'https://cloud.google.com/docs/authentication/workload-identity-federation',
    setupSteps: [
      'Attach a GCP Service Account (or Workload Identity binding) to the K8s pod via annotations.',
      'GKE Workload Identity automatically injects credentials; no file path needed.',
      'Alternatively, set GOOGLE_APPLICATION_CREDENTIALS to the path of a service-account JSON key file.',
      'Set GOOGLE_CLOUD_PROJECT to your GCP project ID.',
      'Leave GEMINI_API_KEY unset — ADC handles auth at the HTTP layer.',
    ],
    vars: ['GEMINI_API_KEY', 'GOOGLE_APPLICATION_CREDENTIALS', 'GOOGLE_CLOUD_PROJECT'],
  },
];

function VarRow({ name, present }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="font-mono text-[11px] text-muted-foreground">{name}</span>
      {present
        ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
        : <XCircle className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />}
    </div>
  );
}

function ProviderBlock({ meta, status }) {
  const [open, setOpen] = useState(false);
  if (!status) return null;

  const wifActive = status[meta.wifVar];
  const staticActive = status[meta.staticVar];
  const authMode = wifActive ? 'WIF / OIDC' : staticActive ? 'Static key' : 'Not configured';
  const modeColor = wifActive
    ? 'text-emerald-500'
    : staticActive
      ? 'text-amber-500'
      : 'text-destructive';

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
      <div
        className="flex items-center gap-2 cursor-pointer select-none"
        onClick={() => setOpen(v => !v)}
        data-testid={`wif-provider-${meta.key}`}
      >
        {open
          ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className={`text-[12px] font-semibold ${meta.color}`}>{meta.label}</span>
        <span className={`ml-auto text-[11px] font-medium ${modeColor}`}>{authMode}</span>
      </div>

      {open && (
        <div className="pl-5 space-y-3">
          <div className="space-y-0.5">
            {meta.vars.map(v => (
              <VarRow key={v} name={v} present={status[v]} />
            ))}
          </div>

          {!wifActive && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                WIF Setup Steps
              </div>
              <ol className="space-y-1 list-decimal list-inside">
                {meta.setupSteps.map((step, i) => (
                  <li key={i} className="text-[11px] text-muted-foreground leading-relaxed">{step}</li>
                ))}
              </ol>
              <a
                href={meta.docs}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
              >
                Official docs <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WifStatusCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWifStatus();
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const wifActive = status?.wif_active;

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4 space-y-3" data-testid="wif-status-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[12.5px] font-semibold">
            Workload Identity Federation (WIF)
            {wifActive != null && (
              <span className={`ml-2 text-[10px] font-normal ${wifActive ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                {wifActive ? '● Active on this pod' : '○ Static-key mode'}
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">
            Replace long-lived static API keys with short-lived OIDC pod credentials.
            When a WIF env var is detected the native client omits the static key header
            and lets the identity provider authenticate the call.
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          className="h-7 w-7 p-0"
          data-testid="wif-status-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {status && PROVIDER_META.map(m => (
        <ProviderBlock key={m.key} meta={m} status={status[m.key]} />
      ))}

      {!status && !loading && (
        <div className="text-[11px] text-muted-foreground">Could not load WIF status.</div>
      )}
    </div>
  );
}
